<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import {
  PUSH_ERRORS,
  PUSH_PERMISSION_STATES,
  getPushPermissionState,
  requestPushPermissions,
} from 'dashboard/helper/pushHelper';

// "Later" hides the prompt for a week. Asking in-app first means agents who
// aren't ready click "Later" here instead of "Block" in the browser prompt,
// which can only be undone from the browser's site settings.
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;

const { t } = useI18n();

const permissionState = ref(getPushPermissionState());
const isDismissed = ref(
  (LocalStorage.get(LOCAL_STORAGE_KEYS.DISMISSED_PUSH_PROMPT) || 0) > Date.now()
);
const isRequesting = ref(false);

const shouldShowBanner = computed(
  () =>
    !isDismissed.value &&
    permissionState.value === PUSH_PERMISSION_STATES.DEFAULT &&
    !!window.chatwootConfig?.vapidPublicKey
);

const dismiss = () => {
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.DISMISSED_PUSH_PROMPT,
    Date.now() + DISMISS_DURATION
  );
  isDismissed.value = true;
};

const onSuccess = () => {
  isRequesting.value = false;
  permissionState.value = getPushPermissionState();
  useAlert(t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_PROMPT.SUCCESS'));
};

const onError = reason => {
  isRequesting.value = false;
  permissionState.value = getPushPermissionState();
  if (reason === PUSH_ERRORS.DISMISSED) {
    dismiss();
    return;
  }
  const messages = {
    [PUSH_ERRORS.DENIED]: t(
      'PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_ERRORS.DENIED'
    ),
    [PUSH_ERRORS.UNSUPPORTED]: t(
      'PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_ERRORS.UNSUPPORTED'
    ),
    [PUSH_ERRORS.NOT_CONFIGURED]: t(
      'PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_ERRORS.NOT_CONFIGURED'
    ),
  };
  useAlert(
    messages[reason] ||
      t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_ERRORS.SUBSCRIBE_FAILED')
  );
};

const enable = () => {
  isRequesting.value = true;
  requestPushPermissions({ onSuccess, onError });
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldShowBanner"
    class="flex items-center justify-between gap-3 px-4 py-2 text-sm border-b bg-n-blue-3 border-n-blue-4 text-n-blue-11"
  >
    <div class="flex items-center min-w-0 gap-2">
      <span class="shrink-0 i-lucide-bell size-4" />
      <span class="truncate">
        {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_PROMPT.MESSAGE') }}
      </span>
    </div>
    <div class="flex items-center gap-1 shrink-0">
      <button
        type="button"
        class="px-3 py-1 rounded-lg whitespace-nowrap bg-n-blue-4 hover:bg-n-blue-5 disabled:opacity-50"
        :disabled="isRequesting"
        @click="enable"
      >
        {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_PROMPT.ENABLE') }}
      </button>
      <button
        type="button"
        class="px-3 py-1 rounded-lg whitespace-nowrap hover:bg-n-blue-4"
        @click="dismiss"
      >
        {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_PROMPT.LATER') }}
      </button>
    </div>
  </div>
</template>
